import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { inr } from "@/lib/utils-format";
import { Plus, Trash2, PackagePlus, PackageMinus, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const UNITS = ["g", "kg", "ml", "ltr", "pcs", "dozen"];
const MOVEMENTS = ["purchase", "adjustment", "wastage"];

const empty = { name: "", unit: "g", cost_per_unit: 0, current_stock: 0, low_stock_threshold: 0, supplier: "", notes: "" };

export default function IngredientsPage() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [stockDialog, setStockDialog] = useState(null); // ingredient obj
  const [movement, setMovement] = useState({ movement_type: "purchase", quantity: 0, unit_cost: 0, reference: "", notes: "" });

  const load = async () => {
    const { data } = await api.get("/ingredients");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.name) return toast.error("Name required");
    try {
      const payload = { ...form, cost_per_unit: Number(form.cost_per_unit) || 0, current_stock: Number(form.current_stock) || 0, low_stock_threshold: Number(form.low_stock_threshold) || 0 };
      if (editingId) await api.patch(`/ingredients/${editingId}`, payload);
      else await api.post("/ingredients", payload);
      toast.success(editingId ? "Ingredient updated" : "Ingredient added");
      setOpen(false); setForm(empty); setEditingId(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const openEdit = (i) => { setForm({ ...empty, ...i }); setEditingId(i.id); setOpen(true); };

  const remove = async (i) => {
    if (!window.confirm(`Delete ${i.name}?`)) return;
    try { await api.delete(`/ingredients/${i.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const submitMovement = async () => {
    try {
      await api.post("/stock-movements", {
        item_type: "ingredient",
        item_id: stockDialog.id,
        movement_type: movement.movement_type,
        quantity: Number(movement.quantity),
        unit_cost: Number(movement.unit_cost) || null,
        reference: movement.reference,
        notes: movement.notes,
      });
      toast.success("Stock updated");
      setStockDialog(null);
      setMovement({ movement_type: "purchase", quantity: 0, unit_cost: 0, reference: "", notes: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-6" data-testid="ingredients-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="uc-label">Raw Materials</div>
          <h1 className="font-[Outfit] text-3xl sm:text-4xl font-semibold tracking-tight mt-1">Ingredients</h1>
        </div>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setForm(empty); setEditingId(null); } }}>
          <DialogTrigger asChild>
            <Button data-testid="ingredient-add-btn" className="bg-[#C05621] hover:bg-[#9C4221] text-white">
              <Plus className="w-4 h-4 mr-1.5" /> New Ingredient
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg bg-white">
            <DialogHeader><DialogTitle className="font-[Outfit]">{editingId ? "Edit" : "New"} Ingredient</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3 py-2">
              <div className="col-span-2"><Label>Name *</Label><Input data-testid="ingredient-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div>
                <Label>Unit</Label>
                <Select value={form.unit} onValueChange={(v) => setForm({ ...form, unit: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">{UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Cost per unit (₹)</Label><Input type="number" value={form.cost_per_unit} onChange={(e) => setForm({ ...form, cost_per_unit: e.target.value })} /></div>
              <div><Label>Current Stock</Label><Input type="number" value={form.current_stock} onChange={(e) => setForm({ ...form, current_stock: e.target.value })} /></div>
              <div><Label>Low-stock at</Label><Input type="number" value={form.low_stock_threshold} onChange={(e) => setForm({ ...form, low_stock_threshold: e.target.value })} /></div>
              <div className="col-span-2"><Label>Supplier</Label><Input value={form.supplier || ""} onChange={(e) => setForm({ ...form, supplier: e.target.value })} /></div>
              <div className="col-span-2"><Label>Notes</Label><Textarea rows={2} value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={submit} data-testid="ingredient-save-btn" className="bg-[#C05621] hover:bg-[#9C4221] text-white">Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white border border-[#EAE5D9] rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left uc-label border-b border-[#EAE5D9]">
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">Unit</th>
              <th className="px-4 py-3 text-right">Cost/Unit</th>
              <th className="px-4 py-3 text-right">Stock</th>
              <th className="px-4 py-3 text-right">Low-stock</th>
              <th className="px-4 py-3">Supplier</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((i) => {
              const low = Number(i.current_stock) <= Number(i.low_stock_threshold);
              return (
                <tr key={i.id} className="border-b border-[#EAE5D9] last:border-0 hover:bg-[#FDFBF7]" data-testid={`ingredient-row-${i.id}`}>
                  <td className="px-4 py-3 font-medium cursor-pointer" onClick={() => openEdit(i)}>{i.name}</td>
                  <td className="px-4 py-3">{i.unit}</td>
                  <td className="px-4 py-3 text-right">{inr(i.cost_per_unit)}</td>
                  <td className={`px-4 py-3 text-right font-semibold ${low ? "text-red-600" : ""}`}>
                    {i.current_stock} {i.unit}
                    {low && <AlertTriangle className="w-3.5 h-3.5 inline ml-1" />}
                  </td>
                  <td className="px-4 py-3 text-right text-[#8B7355]">{i.low_stock_threshold} {i.unit}</td>
                  <td className="px-4 py-3 text-[#5C4033]">{i.supplier || "—"}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-1">
                      <button onClick={() => setStockDialog(i)} data-testid={`ingredient-stock-btn-${i.id}`} title="Stock movement" className="p-1.5 hover:bg-[#F7F2E8] rounded text-[#C05621]"><PackagePlus className="w-4 h-4" /></button>
                      <button onClick={() => remove(i)} className="p-1.5 hover:bg-red-50 text-red-600 rounded"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {items.length === 0 && (
              <tr><td colSpan={7} className="text-center py-10 text-[#8B7355]">No ingredients yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={!!stockDialog} onOpenChange={(o) => !o && setStockDialog(null)}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader><DialogTitle className="font-[Outfit]">Stock Movement — {stockDialog?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label>Type</Label>
              <Select value={movement.movement_type} onValueChange={(v) => setMovement({ ...movement, movement_type: v })}>
                <SelectTrigger data-testid="movement-type-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  {MOVEMENTS.map((m) => <SelectItem key={m} value={m} className="capitalize">{m}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-xs text-[#8B7355] mt-1">Purchase & Adjustment add stock, Wastage removes.</p>
            </div>
            <div><Label>Quantity ({stockDialog?.unit})</Label><Input type="number" value={movement.quantity} onChange={(e) => setMovement({ ...movement, quantity: e.target.value })} data-testid="movement-qty-input" /></div>
            <div><Label>Unit Cost (₹) — optional</Label><Input type="number" value={movement.unit_cost} onChange={(e) => setMovement({ ...movement, unit_cost: e.target.value })} /></div>
            <div><Label>Reference / Bill No.</Label><Input value={movement.reference} onChange={(e) => setMovement({ ...movement, reference: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStockDialog(null)}>Cancel</Button>
            <Button onClick={submitMovement} data-testid="movement-save-btn" className="bg-[#C05621] hover:bg-[#9C4221] text-white">Record</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
